"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useMedia = useMedia;
var _react = require("react");
var _json2mq = _interopRequireDefault(require("json2mq"));
var _tinyInvariant = _interopRequireDefault(require("tiny-invariant"));
var _isSSR = require("../utils/isSSR");
//@ts-ignore

const checkInvariants = _ref => {
  let {
    query,
    queries,
    defaultMatches
  } = _ref;
  (0, _tinyInvariant.default)(query || queries, 'useMedia must be supplied with either "query" or "queries"');
  (0, _tinyInvariant.default)(!(query && queries), 'useMedia cannot be supplied with both "query" and "queries"');
  if (defaultMatches !== undefined) {
    (0, _tinyInvariant.default)(!query || typeof defaultMatches === 'boolean', 'useMedia when query is set, defaultMatches must be a boolean, received ' + typeof defaultMatches);
    (0, _tinyInvariant.default)(!queries || typeof defaultMatches === 'object', 'useMedia when queries is set, defaultMatches must be an object of booleans, received ' + typeof defaultMatches);
  }
};
const wrapInQueryObject = query => ({
  __DEFAULT__: query
});
const unwrapSingleQuery = queryObject => {
  const queryNames = Object.keys(queryObject);
  if (queryNames.length === 1 && queryNames[0] === '__DEFAULT__') {
    return queryObject.__DEFAULT__;
  }
  return queryObject;
};
function resolveQueryString(query) {
  return typeof query !== 'string' ? (0, _json2mq.default)(query) : query;
}
function useMedia(props) {
  const {
    query,
    queries,
    defaultMatches,
    targetWindow
  } = props;
  checkInvariants({
    query,
    queries,
    defaultMatches
  });
  const activeTargetWindow = (0, _isSSR.isSSR)() ? undefined : targetWindow || window;
  const isWindowMatchMediaAvailable = activeTargetWindow === null || activeTargetWindow === void 0 ? void 0 : activeTargetWindow.matchMedia;
  const queryObject = (0, _react.useMemo)(() => {
    if (queries) return queries;
    return wrapInQueryObject(query);
  }, [queries, query]);
  const activeQueries = (0, _react.useRef)([]);
  const [matches, setMatches] = (0, _react.useState)(() => {
    if (!isWindowMatchMediaAvailable) {
      if (defaultMatches !== undefined) return defaultMatches;
      if (query) return true;
      return Object.keys(queries).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
    }
    const initialMatches = {};
    Object.entries(queryObject).forEach(_ref2 => {
      let [key, currentQuery] = _ref2;
      const qs = resolveQueryString(currentQuery);
      initialMatches[key] = activeTargetWindow.matchMedia(qs).matches;
    });
    return initialMatches;
  });
  (0, _react.useEffect)(() => {
    if (!isWindowMatchMediaAvailable) {
      return;
    }
    activeQueries.current = Object.entries(queryObject).map(_ref3 => {
      let [name, currentQuery] = _ref3;
      const qs = resolveQueryString(currentQuery);
      const mq = activeTargetWindow.matchMedia(qs);
      const onChangeHandler = e => {
        setMatches(prevState => {
          return {
            ...prevState,
            [name]: e.matches
          };
        });
      };
      mq.onchange = onChangeHandler;
      return {
        name,
        mq
      };
    });
    return () => {
      // Clear the 'onchange' event listener
      activeQueries.current.forEach(_ref4 => {
        let {
          mq
        } = _ref4;
        mq.onchange = null;
      });
    };
    // Adding dependencies causing destroy effect and run it again
    // The state is not properly updated as onChange run async for each query
    // This creates a known side effect the hook will not work as expected if queries are changed
    // The changes of queries is not expected behavior from the consumers of this hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWindowMatchMediaAvailable]);
  return unwrapSingleQuery(matches);
}